import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { API } from "../lib/api";

// Landing-page "Get on the waitlist" section — captures interested restaurants
// before they can sign up, so we have a lead list when we raise the cap. Also
// serves as social proof ("X restaurants already waiting").
export default function InterestWaitlist() {
  const [form, setForm] = useState({ owner_name: "", business_name: "", email: "", city: "", phone: "", tables: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    fetch(`${API}/interest/stats`).then((r) => r.json()).then((d) => setCount(d.count)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.owner_name.trim() || !form.business_name.trim() || !form.email.trim()) {
      toast.error("Name, restaurant, and email are required.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Signup failed");
      }
      setDone(true);
      setCount((c) => (c === null ? c : c + 1));
    } catch (err) {
      toast.error(err.message || "Couldn't submit — try again in a moment.");
    }
    setBusy(false);
  };

  return (
    <section id="get-on-waitlist" className="px-6 py-16 bg-[#0E1512] text-white relative overflow-hidden" data-testid="interest-waitlist">
      <div className="absolute -right-40 -top-40 w-[600px] h-[600px] rounded-full bg-[#F5C543]/12 blur-3xl pointer-events-none" />
      <div className="absolute inset-0 hex-bg pointer-events-none" style={{opacity:0.06}} />
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-center relative">
        <div>
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#F5C543] font-bold mb-3">Limited beta · {count === null ? "…" : count} restaurants waiting</div>
          <h2 className="font-heading text-3xl md:text-5xl leading-[0.95] tracking-tight mb-4">
            Skip the line. <span className="honey-bar italic text-[#F5C543]">Save the seat.</span>
          </h2>
          <p className="text-sm text-[#C2D1C1] leading-snug mb-3">
            We're capped at 25 restaurants while we make Mimi great. Get on the list — we'll email you the second your spot opens with a discount off your first month.
          </p>
          <p className="text-[11px] font-mono text-white/50">
            No card · 20 seconds · your info stays with us
          </p>
        </div>

        <div className="bg-[#F7F6F2] rounded-2xl ring-1 ring-white/10 p-6 shadow-[0_20px_50px_rgba(0,0,0,0.15)] text-[#121914]">
          {done ? (
            <div className="text-center py-8" data-testid="interest-done">
              <div className="w-14 h-14 rounded-full bg-[#C2D1C1] flex items-center justify-center mx-auto mb-3">
                <CheckCircle size={32} weight="fill" color="#1E3A2F" />
              </div>
              <h3 className="text-xl font-heading font-extrabold mb-2">You're on the list.</h3>
              <p className="text-sm text-[#5C6B61]">We'll email <b>{form.email}</b> the moment a slot opens. Talk soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="interest-form">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="overline block mb-1">Your name*</label>
                  <input className="input-field !py-2" placeholder="Sarah D." value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="interest-owner-name" />
                </div>
                <div>
                  <label className="overline block mb-1">Restaurant*</label>
                  <input className="input-field !py-2" placeholder="Mario's Trattoria" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="interest-business-name" />
                </div>
              </div>
              <div>
                <label className="overline block mb-1">Email*</label>
                <input type="email" className="input-field !py-2" placeholder="you@yourplace.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="interest-email" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="overline block mb-1">City</label>
                  <input className="input-field !py-2" placeholder="Austin, TX" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="interest-city" />
                </div>
                <div>
                  <label className="overline block mb-1">Tables</label>
                  <input className="input-field !py-2" placeholder="20" value={form.tables} onChange={(e) => setForm({ ...form, tables: e.target.value })} data-testid="interest-tables" />
                </div>
              </div>
              <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#0E1512] text-white text-sm font-bold px-5 py-3 rounded-full hover:bg-[#1E3A2F] transition-all disabled:opacity-50" data-testid="interest-submit">
                {busy ? "Adding you…" : "Save my seat"} <ArrowRight size={14} weight="bold" />
              </button>
              <p className="text-[10px] font-mono text-[#5C6B61] text-center">We'll only email about your spot + discount. No spam.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
